import { useState } from 'react';
import { Button, Card, Drawer, Empty, Flex, Space, Tag, Typography } from 'antd';
import { CloseOutlined, EditOutlined, RightOutlined } from '@ant-design/icons';
import InputNumber from 'components/input-number';
import { formatCurrency } from 'utils/number';
import { useOrderEditContext } from '../OrderEditContext';

const { Text, Title } = Typography;

const InfoRow = ({ label, value, children, bordered = true }) => {
    return (
        <Flex
            justify='space-between'
            align='center'
            gap={12}
            style={{
                padding: '10px 0',
                borderBottom: bordered ? '1px solid #f5f5f5' : 'none',
            }}
        >
            <Text>{label}</Text>
            <div style={{ textAlign: 'right' }}>
                {children ?? <Text>{value}</Text>}
            </div>
        </Flex>
    );
};

const OrderLineItemsCardMobile = () => {
    const { lineItems, onLineQuantityChange, onLinePriceChange, setLineItemPriceType } = useOrderEditContext();
    const [editProductId, setEditProductId] = useState(null);

    const editLine = lineItems.find(x => x.product_id === editProductId) ?? null;

    const closeDrawer = () => setEditProductId(null);

    const onRemoveLine = () => {
        onLineQuantityChange(editProductId, 0);
        closeDrawer();
    };

    const totalQuantity = lineItems.reduce((sum, line) => sum + (line.quantity || 0), 0);

    return (
        <>
            <Card size='small'>
                <Space direction='vertical' size={12} style={{ width: '100%' }}>
                    <Flex justify='space-between' align='center' gap={8}>
                        <Text>{lineItems.length} sản phẩm</Text>
                        <Tag color='blue'>SL {totalQuantity}</Tag>
                    </Flex>

                    {lineItems.length ? (
                        <Space direction='vertical' size={10} style={{ width: '100%' }}>
                            {lineItems.map((line) => (
                                <Card
                                    key={line.product_id}
                                    size='small'
                                    styles={{ body: { padding: 12 } }}
                                    onClick={() => setEditProductId(line.product_id)}
                                    style={{ borderRadius: 12, cursor: 'pointer' }}
                                >
                                    <Space direction='vertical' size={10} style={{ width: '100%' }}>
                                        <Flex justify='space-between' align='flex-start' gap={12}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Title level={5} style={{ margin: 0 }} ellipsis={{ rows: 2, tooltip: `${line.product?.name || ''} ${line.variant?.variant_title || ''}` }}>
                                                    {line.product?.name} {line.variant?.variant_title}
                                                </Title>
                                            </div>
                                            <RightOutlined style={{ color: '#bfbfbf', marginTop: 4 }} />
                                        </Flex>

                                        <Flex justify='space-between' align='center' gap={8} wrap='wrap'>
                                            <Tag>SL {line.quantity}</Tag>
                                            <Text>{formatCurrency(line.amount)}</Text>
                                        </Flex>

                                        <Flex justify='space-between' align='center' gap={8}>
                                            <Text>Đơn giá</Text>
                                            <Text>{formatCurrency(line.price)}</Text>
                                        </Flex>
                                    </Space>
                                </Card>
                            ))}
                        </Space>
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='Chưa có sản phẩm trong đơn' />
                    )}
                </Space>
            </Card>

            <Drawer
                title='Chỉnh sửa sản phẩm'
                placement='bottom'
                height='auto'
                open={!!editLine}
                onClose={closeDrawer}
                styles={{ body: { paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' } }}
            >
                {editLine && (
                    <Space direction='vertical' size={12} style={{ width: '100%' }}>
                        <Card size='small' styles={{ body: { padding: 12 } }}>
                            <Space direction='vertical' size={12} style={{ width: '100%' }}>
                                <div>
                                    <Title level={5} style={{ margin: 0 }}>
                                        {editLine.product?.name} {editLine.variant?.variant_title}
                                    </Title>
                                </div>

                                <InfoRow label='Số lượng'>
                                    <InputNumber
                                        value={editLine.quantity}
                                        showCalculator={false}
                                        onChange={value => {
                                            if (value > 0) {
                                                onLineQuantityChange(editLine.product_id, value);
                                            }
                                        }}
                                    />
                                </InfoRow>

                                <InfoRow label='Đơn giá'>
                                    {editLine.price_type === '2' ? (
                                        <InputNumber
                                            value={editLine.price}
                                            onChange={value => onLinePriceChange(editLine.product_id, value)}
                                        />
                                    ) : (
                                        <Space size={4}>
                                            <Text>{formatCurrency(editLine.price)}</Text>
                                            <Button type='text' onClick={() => setLineItemPriceType(editLine.product_id, '2')}>
                                                <EditOutlined />
                                            </Button>
                                        </Space>
                                    )}
                                </InfoRow>

                                <InfoRow label='Thành tiền' bordered={false}>
                                    <Text strong>{formatCurrency(editLine.amount)}</Text>
                                </InfoRow>
                            </Space>
                        </Card>

                        <Flex justify='space-between' gap={8}>
                            <Button danger icon={<CloseOutlined />} onClick={onRemoveLine}>
                                Xoá sản phẩm
                            </Button>
                            <Button type='primary' onClick={closeDrawer}>
                                Xong
                            </Button>
                        </Flex>
                    </Space>
                )}
            </Drawer>
        </>
    );
};

export default OrderLineItemsCardMobile;
